import { Component, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  first,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { Constants } from 'src/app/core/constants';
import { AssignmentStatuses } from 'src/app/core/enums/assignment-statuses.enum';
import {
  Project,
  ProjectAssignment,
} from 'src/app/core/models/project/project.interface';
import { AssignmentService } from 'src/app/core/services/assignment.service';
import { FileService } from 'src/app/core/services/file.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { ProjectForUpsert } from 'src/app/core/models/project/project.model';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { AssignmentsFilterForm } from 'src/app/core/models/project/assignments-filter-form.model';
import { ActionMenuItem } from 'src/app/core/components/action-menu/action-menu-item.interface';
import { RequestModel } from 'src/app/core/models/request-model.interface';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { Util } from 'src/app/core/utils/util';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { TranslateService } from '@ngx-translate/core';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { PermissionService } from 'src/app/core/services/permission.service';
import { AssignmentPriorities } from 'src/app/core/enums/assignment-priorities.enum';
import { ProjectEditResult } from 'src/app/core/models/project/project-edit-result.model';

interface AssignmentFilterOption {
  readonly status: AssignmentStatuses;
  readonly controlName: 'new' | 'active' | 'completed' | 'postponed' | 'overdue';
  readonly translationKey: string;
  readonly chipClass: string;
}

@Component({
    selector: 'app-project-overview',
    templateUrl: './project-overview.component.html',
    styleUrls: ['./project-overview.component.scss'],
    providers: [AssignmentsFilterForm],
    standalone: false
})
export class ProjectOverviewComponent implements OnDestroy {
  readonly filterForm = inject(AssignmentsFilterForm);
  private readonly fileService = inject(FileService);
  private readonly projectService = inject(ProjectService);
  private readonly employeeService = inject(EmployeeService);
  private readonly assignmentService = inject(AssignmentService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);

  public readonly notFound = Constants.NotFound;
  public readonly uaDateWithTimeFormat = Constants.DateFormats.get('ua-short');
  public readonly uaDateFormat = Constants.DateFormats.get('ua-date');
  public readonly assignmentStatuses = AssignmentStatuses;
  public readonly assignmentPriorities = AssignmentPriorities;
  public readonly currentLang: string;
  public readonly assignmentPriorityBadgeClassMap: Record<number, string> = {
    [AssignmentPriorities.Critical]: 'assignment-priority-mark--critical',
    [AssignmentPriorities.High]: 'assignment-priority-mark--high',
    [AssignmentPriorities.Middle]: 'assignment-priority-mark--middle',
    [AssignmentPriorities.Low]: 'assignment-priority-mark--low',
  };
  public readonly assignmentFilterOptions: AssignmentFilterOption[] = [
    {
      status: AssignmentStatuses.Overdue,
      controlName: 'overdue',
      translationKey: 'ASSIGNMENT_STATUSES_PLURAL.OVERDUE',
      chipClass: 'status-overdue',
    },
    {
      status: AssignmentStatuses.Active,
      controlName: 'active',
      translationKey: 'ASSIGNMENT_STATUSES_PLURAL.ACTIVE',
      chipClass: 'status-active',
    },
    {
      status: AssignmentStatuses.New,
      controlName: 'new',
      translationKey: 'ASSIGNMENT_STATUSES_PLURAL.NEW',
      chipClass: 'status-new',
    },
    {
      status: AssignmentStatuses.Postponed,
      controlName: 'postponed',
      translationKey: 'ASSIGNMENT_STATUSES_PLURAL.POSTPONED',
      chipClass: 'status-postponed',
    },
    {
      status: AssignmentStatuses.Completed,
      controlName: 'completed',
      translationKey: 'ASSIGNMENT_STATUSES_PLURAL.COMPLETED',
      chipClass: 'status-completed',
    },
  ];

  public readonly emptyAssignmentListParam: Record<string, string> = {
    uk: 'задач',
    en: 'assignment',
  };

  public readonly canEditProject$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Project,
          UserCompanyPermissions.Edit,
        ),
      ),
      shareReplay(),
    );

  public readonly canDeleteProject$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Project,
          UserCompanyPermissions.Delete,
        ),
      ),
      shareReplay(),
    );

  public readonly canCreateAssignment$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Assignment,
          UserCompanyPermissions.Create,
        ),
      ),
      shareReplay(),
    );

  public readonly canOpenProjectActions$ = combineLatest([
    this.canEditProject$,
    this.canDeleteProject$,
  ]).pipe(
    map(([canEditProject, canDeleteProject]) => canEditProject || canDeleteProject),
    shareReplay(1),
  );
  public readonly projectActionItemsActive: ActionMenuItem[] = [
    { id: 'edit', labelKey: 'BUTTONS.EDIT', icon: 'edit' },
    { id: 'archive', labelKey: 'BUTTONS.ARCHIVE', icon: 'archive' },
    { id: 'delete', labelKey: 'BUTTONS.DELETE_PROJECT', icon: 'delete', danger: true, dividerBefore: true },
  ];
  public readonly projectActionItemsArchived: ActionMenuItem[] = [
    { id: 'edit', labelKey: 'BUTTONS.EDIT', icon: 'edit' },
    { id: 'unarchive', labelKey: 'BUTTONS.UNARCHIVE', icon: 'archive' },
    { id: 'delete', labelKey: 'BUTTONS.DELETE_PROJECT', icon: 'delete', danger: true, dividerBefore: true },
  ];
  public readonly projectActionItemsEditOnlyActive: ActionMenuItem[] = [
    { id: 'edit', labelKey: 'BUTTONS.EDIT', icon: 'edit' },
    { id: 'archive', labelKey: 'BUTTONS.ARCHIVE', icon: 'archive' },
  ];
  public readonly projectActionItemsEditOnlyArchived: ActionMenuItem[] = [
    { id: 'edit', labelKey: 'BUTTONS.EDIT', icon: 'edit' },
    { id: 'unarchive', labelKey: 'BUTTONS.UNARCHIVE', icon: 'archive' },
  ];
  public readonly projectActionItemsDeleteOnly: ActionMenuItem[] = [
    { id: 'delete', labelKey: 'BUTTONS.DELETE_PROJECT', icon: 'delete', danger: true },
  ];

  public assignmentStatusValues = Object.values(AssignmentStatuses).filter(
    (value) => !isNaN(Number(value)),
  );

  public project$ = new BehaviorSubject<Project>({
    id: '',
    title: '',
    color: '',
    isArchived: false,
    employeeProfiles: [],
    hiddenEmployees: [],
  });

  public projectAssignments$: Observable<PagedList<ProjectAssignment>>;
  public assignmentPerformerProfiles: Record<string, MinimalProfile> = {};
  public teamPreviewMembers: MinimalProfile[] = [];
  public teamOverflowMembers: MinimalProfile[] = [];
  public isCreateAssignmentModalOpen = false;
  public isFilesModalOpen = false;
  public isEditProjectModalOpen = false;
  public editingProject: Project | null = null;

  public companyId = '';
  public projectsBackUrl = '';
  public assignmentBackQueryParams: Params = {};
  private projectId = '';

  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [
      {
        field: 'status',
        operator: 'in',
        value: '',
        values: [
          AssignmentStatuses.New.toString(),
          AssignmentStatuses.Completed.toString(),
          AssignmentStatuses.Active.toString(),
          AssignmentStatuses.Postponed.toString(),
          AssignmentStatuses.Overdue.toString(),
        ],
      },
      {
        field: 'isArchived',
        operator: 'eq',
        value: 'false',
        values: [],
      },
    ],
    sortColumn: 'created_at',
    sortOrder: 'desc',
  };

  private initUrls$: Observable<Params>;
  private unsubscribe$ = new Subject<void>();

  constructor() {
    const route = this.route;

    this.initUrls$ = route.params.pipe(
      first(),
      tap((params) => {
        this.companyId = route.parent?.parent?.snapshot.params['id'];
        this.projectId = params['id'];
        this.projectsBackUrl = `/companies/${this.companyId}/projects`;
        this.assignmentBackQueryParams = {
          backUrl: `/companies/${this.companyId}/projects/${this.projectId}`,
        };

        this.projectService.setProjectsUrl(this.companyId);
        this.employeeService.setEmployeesUrl(this.companyId);
        this.assignmentService.setAssignmentsUrl(this.projectId);
      }),
      shareReplay(1),
    );

    const projectWithImage$ = this.getProjectWithImage();
    const projectWithEmployees$ =
      this.getProjectWithEmployees$(projectWithImage$);

    projectWithEmployees$.subscribe();

    this.projectAssignments$ = this.initUrls$.pipe(
      switchMap(() =>
        this.assignmentService.getProjectAssignments(this.request),
      ),
      shareReplay(1),
    );

    this.filterForm.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((formValue) => {
          const request = this.request;
          request.filters[0].values = this.getCheckedStatuses(formValue);

          const isArchivedFilter = request.filters.find(
            (f) => f.field === 'isArchived',
          )!;

          isArchivedFilter.value = formValue.isArchived.toString();

          request.sortColumn = formValue.sortColumn;
          request.sortOrder = formValue.sortOrder === true ? 'desc' : 'asc';

          this.projectAssignments$ = this.assignmentService
            .getProjectAssignments(request)
            .pipe(shareReplay(1));
        }),
      )
      .subscribe();

    this.currentLang =
      this.translateService.currentLang ?? Constants.DefaultLanguage;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public deleteAssignment(projectId: string, assignmentId: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'задачу',
        en: 'assignment',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => this.assignmentService.deleteAssignment(assignmentId)),
        switchMap(() =>
          this.fileService.delete(
            `companies/${this.companyId}/projects/${projectId}/assignments/${assignmentId}`,
          ),
        ),
        tap(() => {
          this.projectAssignments$ = this.assignmentService
            .getProjectAssignments(this.request)
            .pipe(shareReplay(1));
        }),
      )
      .subscribe();
  }

  public deleteProject(id: string): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'проєкт',
        en: 'project',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter((res) => !!res),
        switchMap(() => this.projectService.deleteProject(id)),
        switchMap(() =>
          this.fileService.delete(`companies/${this.companyId}/projects/${id}`),
        ),
        tap(() =>
          this.router.navigateByUrl(`companies/${this.companyId}/projects`),
        ),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public editProject(project: Project): void {
    this.editingProject = project;
    this.isEditProjectModalOpen = true;
  }

  public handleProjectAction(actionId: string, project: Project): void {
    if (actionId === 'edit') {
      this.editProject(project);
      return;
    }

    if (actionId === 'archive') {
      this.archiveProject(project.id);
      return;
    }

    if (actionId === 'unarchive') {
      this.unarchiveProject(project.id);
      return;
    }

    if (actionId === 'delete') {
      this.deleteProject(project.id);
    }
  }

  public closeEditProjectModal(): void {
    this.isEditProjectModalOpen = false;
    this.editingProject = null;
  }

  public onEditProjectSave(res: ProjectEditResult): void {
    const project = this.editingProject;
    if (!project) {
      return;
    }

    const projectForUpdate = res.formData as ProjectForUpsert;
    projectForUpdate.employeesIds = res.employeeProfiles.map(
      (employee: MinimalProfile) => employee.id,
    );

    const extensionName = Util.getExtensionFromFileTitle(res.projectImage?.name);
    const imageExtension = extensionName
      ? `.${extensionName}`
      : project.titleImageExtension;

    projectForUpdate.titleImageExtension = imageExtension;

    const updateProjectRequest = this.projectService.updateProject(
      project.id,
      projectForUpdate,
    );

    const updatedProject: Project = {
      id: project.id,
      title: projectForUpdate.title,
      description: projectForUpdate.description,
      tag: projectForUpdate.tag,
      imageUrl: project.imageUrl,
      employeeProfiles: res.employeeProfiles.slice(
        0,
        Constants.DefaultLoadedProjectEmployees,
      ),
      hiddenEmployees: res.employeeProfiles.slice(
        Constants.DefaultLoadedProjectEmployees,
      ),
      titleImageExtension: imageExtension,
      color: project.color,
      isArchived: project.isArchived,
      organization: res.organization ?? project.organization,
    };

    combineLatest([of(updatedProject), updateProjectRequest])
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(([freshProject]) => {
          if (
            !freshProject.titleImageExtension
            || freshProject.titleImageExtension === Constants.NotFound
          ) {
            freshProject.titleImageExtension = Constants.NotFound;
            freshProject.imageUrl = Constants.NotFound;
            return of(freshProject);
          }

          return this.fileService.getEntityWithImageUrl(
            freshProject,
            `companies/${this.companyId}/projects/${project.id}/title${freshProject.titleImageExtension}`,
            true,
          );
        }),
        tap((freshProject) => {
          this.updateAssignmentPerformerProfiles(freshProject);
          this.project$.next(freshProject);
          this.closeEditProjectModal();
        }),
        catchError((error) => {
          console.log(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public navigateToAssignment(id: string): void {
    this.router.navigate(['assignments', id], {
      relativeTo: this.route,
      queryParams: this.assignmentBackQueryParams,
    });
  }

  public openCreateAssignmentModal(): void {
    this.isCreateAssignmentModalOpen = true;
  }

  public closeCreateAssignmentModal(): void {
    this.isCreateAssignmentModalOpen = false;
  }

  public onAssignmentCreated(assignmentId: string): void {
    this.closeCreateAssignmentModal();
    this.navigateToAssignment(assignmentId);
  }

  public onArchive(assignmentId: string): void {
    this.assignmentService
      .archiveAssignment(assignmentId)
      .pipe(
        first(),
        tap(() => {
          this.projectAssignments$ = this.assignmentService
            .getProjectAssignments(this.request)
            .pipe(shareReplay(1));
        }),
      )
      .subscribe();
  }

  public onUnarchive(assignmentId: string): void {
    this.assignmentService
      .unarchiveAssignment(assignmentId)
      .pipe(
        first(),
        tap(() => {
          this.projectAssignments$ = this.assignmentService
            .getProjectAssignments(this.request)
            .pipe(shareReplay(1));
        }),
      )
      .subscribe();
  }

  public archiveProject(projectId: string): void {
    this.projectService
      .archiveProject(projectId)
      .pipe(
        first(),
        tap(() => {
          this.project$.next({ ...this.project$.getValue(), isArchived: true });
        }),
      )
      .subscribe();
  }

  public unarchiveProject(projectId: string): void {
    this.projectService
      .unarchiveProject(projectId)
      .pipe(
        first(),
        tap(() => {
          this.project$.next({
            ...this.project$.getValue(),
            isArchived: false,
          });
        }),
      )
      .subscribe();
  }

  public onPageChange(page: number): void {
    this.request.page = page;

    this.projectAssignments$ = this.assignmentService
      .getProjectAssignments(this.request)
      .pipe(shareReplay(1));
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;

    this.projectAssignments$ = this.assignmentService
      .getProjectAssignments(this.request)
      .pipe(shareReplay(1));
  }

  public openFilesModal(): void {
    this.isFilesModalOpen = true;
  }

  public closeFilesModal(): void {
    this.isFilesModalOpen = false;
  }

  private getProjectWithImage(): Observable<Project> {
    return this.initUrls$.pipe(
      switchMap(() => this.projectService.getProject(this.projectId)),
      switchMap((project) => {
        if (!project.titleImageExtension) {
          project.imageUrl = Constants.NotFound;
          return of(project);
        }

        return this.fileService.getEntityWithImageUrl(
          project,
          `companies/${this.companyId}/projects/${project.id}/title${project.titleImageExtension}`,
        );
      }),
    );
  }

  private getProjectWithEmployees$(
    project$: Observable<Project>,
  ): Observable<Project> {
    return project$.pipe(
      switchMap((project) => {
        const employeeProfiles$ = forkJoin(
          project.employeeProfiles
            .slice(0, Constants.DefaultLoadedProjectEmployees)
            .map((ep) => this.getEmployeeProfileWithImage$(ep)),
        );

        const hiddenEmployees$ =
          project.employeeProfiles.length >
          Constants.DefaultLoadedProjectEmployees
            ? forkJoin(
                project.employeeProfiles
                  .slice(Constants.DefaultLoadedProjectEmployees)
                  .map((ep) => this.getEmployeeProfileWithImage$(ep)),
              )
            : of([]);

        return forkJoin({
          employeeProfiles: employeeProfiles$,
          hiddenEmployees: hiddenEmployees$,
        }).pipe(
          map(({ employeeProfiles, hiddenEmployees }) => {
            project.employeeProfiles = employeeProfiles;
            project.hiddenEmployees = hiddenEmployees;

            return project;
          }),
        );
      }),
      tap((project) => {
        this.updateAssignmentPerformerProfiles(project);
        this.project$.next(project);
      }),
      catchError((error) => {
        console.error(error);
        return EMPTY;
      }),
    );
  }

  private getEmployeeProfileWithImage$(
    profile: MinimalProfile,
  ): Observable<MinimalProfile> {
    if (!profile.avatarImageExtension) {
      profile.imageUrl = Constants.NotFound;
      return of(profile);
    }

    return this.fileService.getEntityWithImageUrl(
      profile,
      `users/${profile.id}/avatar${profile.avatarImageExtension}`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getCheckedStatuses(formValue: any): string[] {
    const statusMap: Record<string, AssignmentStatuses> = {
      new: AssignmentStatuses.New,
      completed: AssignmentStatuses.Completed,
      active: AssignmentStatuses.Active,
      postponed: AssignmentStatuses.Postponed,
      overdue: AssignmentStatuses.Overdue,
    };

    return Object.entries(statusMap)
      .filter(([key]) => formValue[key] === true)
      .map(([, status]) => status.toString());
  }

  private updateAssignmentPerformerProfiles(project: Project): void {
    const allProfiles = [
      ...(project.employeeProfiles ?? []),
      ...(project.hiddenEmployees ?? []),
    ];

    this.teamPreviewMembers = allProfiles.slice(0, 5);
    this.teamOverflowMembers = allProfiles.slice(5);

    this.assignmentPerformerProfiles = allProfiles.reduce(
      (accumulator, profile) => {
        accumulator[profile.id] = profile;
        return accumulator;
      },
      {} as Record<string, MinimalProfile>,
    );
  }
}
